import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from "react-router-dom";
import get from 'lodash/get';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import clone from 'lodash/clone';
import QRCode from 'react-qr-code';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useAuth } from 'contexts/auth';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import DataTable from 'components/DataTable';
import { withLoading } from 'utils/hoc';
import { handleErrorResponse, parseJSON } from 'utils/general';
import { updateDatum, deleteDatum, getData, sendApiRequest } from 'api/general';

const ID_FIELD = 'iwatch_account_id';
const PAGE_OPTIONS = [10, 20, 50, 100];

const WhatsappSourceAccountListSection = ({
  title,
  title_font_size,
  title_color,
  table_name,
  id_field,
  columns_config,
  column_mapping_config,
  value_mapping_config,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  page_options,
  extra_params,
  add_data_path,
  edit_data_path,
  addable,
  editable,
  deletable,
  selectable,
  support_pagination,
  show_data_count,
  container_props,
  setLoading,
  ...props
}) => {
  const { setAuth } = useAuth();
  const [rowsData, setRowsData] = useState([]);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const onToggleActive = (row) => {
    return updateDatum('/iwatch_accounts', row.iwatch_account_id, {
      active: !row.active
    }, 'bearer');
  }

  const [waStatusMap, setWaStatusMap] = useState({});
  const [qrSession, setQrSession] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const qrPollRef = useRef(null);

  const fetchWaStatus = useCallback((accountId) => {
    return sendApiRequest(`/iwatch_account_sessions/${accountId}/status`, 'get', null, 'bearer')
      .then(({ data }) => {
        const chip = data.state === 'connected' ? 'connected'
          : data.linking ? 'linking'
          : data.pairing ? 'pairing'
          : data.state;
        setWaStatusMap((prev) => ({ ...prev, [accountId]: { state: chip, since: data.since } }));
      })
      .catch(() => {
        setWaStatusMap((prev) => ({ ...prev, [accountId]: { state: 'no_feeder' } }));
      });
  }, []);

  useEffect(() => {
    if (!rowsData.length) return undefined;
    rowsData.forEach((row) => fetchWaStatus(row.iwatch_account_id));
    const interval = setInterval(() => {
      rowsData.forEach((row) => fetchWaStatus(row.iwatch_account_id));
    }, 15000);
    return () => clearInterval(interval);
  }, [rowsData, fetchWaStatus]);

  const stopQrPolling = useCallback(() => {
    if (qrPollRef.current) {
      clearInterval(qrPollRef.current);
      qrPollRef.current = null;
    }
  }, []);

  const startReconnect = useCallback((account) => {
    const accountId = account.iwatch_account_id;
    setQrSession({ account, qr: null, connected: false, pending: true });
    stopQrPolling();
    qrPollRef.current = setInterval(() => {
      sendApiRequest(`/iwatch_account_sessions/${accountId}/qr`, 'get', null, 'bearer')
        .then(({ data }) => {
          if (data.connected) {
            stopQrPolling();
            setQrSession((s) => (s ? { ...s, connected: true, linking: false, pending: false } : s));
            fetchWaStatus(accountId);
          } else if (data.linking) {
            // phone scanned and is linking -- freeze the QR display so it
            // does not keep rotating while the login is in flight
            setQrSession((s) => (s ? { ...s, linking: true, qr: null, pending: false } : s));
          } else if (data.qr) {
            setQrSession((s) => (s ? { ...s, qr: data.qr, pending: false } : s));
          }
        })
        .catch(() => {
          setQrSession((s) => (s ? { ...s, pending: true } : s));
        });
    }, 4000);
  }, [fetchWaStatus, stopQrPolling]);

  const closeQrSession = useCallback(() => {
    if (qrSession && !qrSession.connected) {
      sendApiRequest(`/iwatch_account_sessions/${qrSession.account.iwatch_account_id}/stop_pair`, 'post', null, 'bearer').catch(() => {});
    }
    stopQrPolling();
    setQrSession(null);
  }, [qrSession, stopQrPolling]);

  const onConfirmDisconnect = useCallback(() => {
    if (!disconnectTarget) return;
    sendApiRequest(`/iwatch_account_sessions/${disconnectTarget.iwatch_account_id}/disconnect`, 'post', null, 'bearer')
      .then(() => fetchWaStatus(disconnectTarget.iwatch_account_id))
      .catch(() => {})
      .finally(() => setDisconnectTarget(null));
  }, [disconnectTarget, fetchWaStatus]);

  const WA_STATE_DISPLAY = {
    connected: { label: 'Online', color: '#1e7e34', bg: '#e7f6ec', dot: '#28a745' },
    logged_out: { label: 'Offline', color: '#b71c1c', bg: '#fdecea', dot: '#d32f2f' },
    down: { label: 'Offline', color: '#b71c1c', bg: '#fdecea', dot: '#d32f2f' },
    starting: { label: 'Connecting', color: '#e65100', bg: '#fff3e0', dot: '#fb8c00' },
    pairing: { label: 'Pairing…', color: '#e65100', bg: '#fff3e0', dot: '#fb8c00' },
    linking: { label: 'Linking…', color: '#e65100', bg: '#fff3e0', dot: '#fb8c00' },
    unknown: { label: 'Unknown', color: '#777', bg: '#eee', dot: '#bbb' },
    no_feeder: { label: 'No feeder', color: '#777', bg: '#eee', dot: '#bbb' },
  };

  const waColumn = {
    field: 'wa_session',
    label: 'WhatsApp',
    is_non_sortable: true,
    renderContent: (data) => {
      const status = waStatusMap[data.iwatch_account_id];
      const state = status?.state || 'unknown';
      const display = WA_STATE_DISPLAY[state] || WA_STATE_DISPLAY.unknown;
      const connected = state === 'connected';
      const noFeeder = state === 'no_feeder';
      return (
        <MKBox display="flex" flexDirection="column" alignItems="center" gap="4px">
          <MKBox
            display="inline-flex"
            alignItems="center"
            gap="5px"
            sx={{ backgroundColor: display.bg, color: display.color, fontSize: '0.75rem', fontWeight: 600, borderRadius: '10px', padding: '3px 9px' }}
          >
            <MKBox sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: display.dot }} />
            {display.label}
          </MKBox>
          <Button
            size="small"
            variant={connected ? 'outlined' : 'gradient'}
            color={connected ? 'error' : 'primary'}
            disabled={noFeeder}
            onClick={() => (connected ? setDisconnectTarget(data) : startReconnect(data))}
          >
            {connected ? 'Disconnect' : 'Reconnect'}
          </Button>
        </MKBox>
      );
    }
  };
  
  const [formattedTableColumns, setFormattedTableColumns] = useState([{
    field: 'name',
    label: 'Name',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="start" flexDirection="row" alignItems="center">
        <Link
          style={{
            fontSize: '0.875rem',
            color: '#0094ff'
          }}
          to={`/source_information/groups?${encodeURIComponent(ID_FIELD)}=${encodeURIComponent(data[ID_FIELD])}`}
        >
          {data.name}
        </Link>
      </MKBox>;
    }
  }, {
    field: 'phone',
    label: 'Phone',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <Link
          style={{
            fontSize: '0.875rem',
            color: '#0094ff'
          }}
          to={`/source_information/groups?${encodeURIComponent(ID_FIELD)}=${encodeURIComponent(data[ID_FIELD])}`}
        >
          {data.phone}
        </Link>
      </MKBox>;
    }
  }, {
    field: 'active',
    label: 'Active',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.active ? 'Active' : 'Inactive'}
        </MKTypography>
      </MKBox>;
    }
  }, {
      field: 'iwatch_account_id',
      label: 'Toggle active status',
      renderContent: (data) => {
        return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
          <Button
            size="small"
            variant="gradient"
            onClick={() => {
              onToggleActive(data).then((res) => {
                const newDatum = res.data;
                const id = newDatum.iwatch_account_id;
                setRowsData((rowList) => {
                  let newRowList;
                  const index = findIndex(rowList, ['iwatch_account_id', id]);
                  if (index === -1) {
                    newRowList = rowList;
                  } else {
                    newRowList = clone(rowList);
                    newRowList[index] = newDatum;
                  }
                  return newRowList;
                });
              });
            }}
          >
            Toggle
          </Button>
        </MKBox>
        
      }
    }]);
  const navigate = useNavigate();

  const fetchDataFromApi = useCallback(() => {
    setLoading(true);
    const dataParams = {
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
      $skip: currentPage * pageSize,
      $top: pageSize,
    };
    return getData('/iwatch_accounts', dataParams, 'bearer')
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setRowsData(data);
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      }).finally(() => {
        setLoading(false);
      });
  }, [currentPage, pageSize, setAuth, setLoading, sortBy, sortDirection]);

  useEffect(() => {
    fetchDataFromApi();
  }, [fetchDataFromApi]);

  const onClickSort = useCallback((headerName) => {
    const column = find(formattedTableColumns, ['field', headerName]);
    if (column && !column.is_non_sortable) {
      if (sortBy === headerName) {
        setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(headerName);
        setSortDirection('asc');
      }
    }
  }, [formattedTableColumns, sortBy]);

  const deleteFromApi = useCallback((dataId) => {
    // setLoading(true);
    return deleteDatum('iwatch_accounts', dataId, 'bearer')
      .then(() => {
        return fetchDataFromApi();
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [fetchDataFromApi, setAuth]);

  const onClickAdd = useCallback(() => {
    navigate('/source_information/add');
  }, [navigate]);

  const onClickEdit = useCallback((dataId) => {
    navigate(`/source_information/edit?${encodeURIComponent(ID_FIELD)}=${encodeURIComponent(dataId)}`);
  }, [navigate]);

  return <MKBox
    sx={{
      ...parseJSON(container_props, {}),
    }}
    {...props}
  >

    <MKBox mb={2} display="flex" alignItems="center">
      <MKTypography mr="1rem" color={title_color == null ? '#000' : title_color} fontSize="20px">
        Source Feeder Account(s) ({totalRows})
      </MKTypography>
    </MKBox>

    <DataTable
      data={rowsData}
      columns={[...formattedTableColumns, waColumn]}
      headerBackgroundColor={header_background_color == null ? '#f2f3f7' : header_background_color}
      headerFontColor={header_font_color == null ? '#0094ff' : header_font_color}
      oddRowBackgroundColor={odd_row_background_color == null ? '#eeeeee' : odd_row_background_color}
      oddRowFontColor={odd_row_font_color == null ? '#000' : odd_row_font_color}
      evenRowBackgroundColor={even_row_background_color == null ? '#fff' : even_row_background_color}
      evenRowFontColor={even_row_font_color == null ? '#000' : even_row_font_color}
      idField={ID_FIELD}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onClickSort}
      paginationDisabled={null}
      onClickRow={null}
      onPressAdd={null}
      onPressEdit={null}
      onPressDelete={null}
      selectable={true}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      rowsPerPage={pageSize}
      rowsPerPageOptions={PAGE_OPTIONS}
      totalCount={totalRows}
      onRowsPerPageChange={setPageSize}
    />

    <Dialog open={!!qrSession} onClose={closeQrSession} maxWidth="xs" fullWidth>
      <DialogContent>
        <MKBox display="flex" flexDirection="column" alignItems="center" py={1}>
          <MKTypography fontSize="16px" fontWeight="bold" mb={0.5}>
            {qrSession ? `Reconnect - ${qrSession.account.name}` : ''}
          </MKTypography>
          <MKTypography fontSize="12px" color="gray" mb={2}>
            {qrSession?.account?.phone}
          </MKTypography>
          {qrSession?.connected ? (
            <MKBox display="flex" flexDirection="column" alignItems="center" py={3}>
              <MKTypography fontSize="15px" style={{ color: '#1e7e34', fontWeight: 600 }}>
                Connected to WhatsApp
              </MKTypography>
              <MKTypography fontSize="12px" color="gray" mt={1}>
                The feeder will resume syncing automatically.
              </MKTypography>
            </MKBox>
          ) : qrSession?.linking ? (
            <MKBox display="flex" flexDirection="column" alignItems="center" py={3}>
              <MKTypography fontSize="15px" style={{ color: '#e65100', fontWeight: 600 }}>
                Linking on your phone…
              </MKTypography>
              <MKTypography fontSize="12px" color="gray" mt={1} textAlign="center">
                QR code received — WhatsApp is completing the sign-in. Keep the dialog open.
              </MKTypography>
            </MKBox>
          ) : (
            <>
              <MKBox
                mb={1.5}
                p={1.5}
                sx={{ border: '1px solid #e5e5e5', borderRadius: '8px', backgroundColor: '#fff' }}
              >
                {qrSession?.qr ? (
                  <QRCode value={qrSession.qr} size={220} />
                ) : (
                  <MKBox display="flex" alignItems="center" justifyContent="center" sx={{ width: 220, height: 220 }}>
                    <MKTypography fontSize="13px" color="gray">
                      {qrSession?.pending ? 'Requesting QR code…' : 'Loading…'}
                    </MKTypography>
                  </MKBox>
                )}
              </MKBox>
              <MKTypography fontSize="12px" color="gray" textAlign="center" mb={2}>
                On the phone for this account: WhatsApp → Settings → Linked Devices → Link a Device.
                The code refreshes automatically while this dialog is open.
              </MKTypography>
            </>
          )}
          <MKBox display="flex" gap={1} width="100%">
            <Button fullWidth variant="outlined" onClick={closeQrSession}>
              {qrSession?.connected ? 'Done' : 'Cancel'}
            </Button>
          </MKBox>
        </MKBox>
      </DialogContent>
    </Dialog>

    <Dialog open={!!disconnectTarget} onClose={() => setDisconnectTarget(null)} maxWidth="xs" fullWidth>
      <DialogContent>
        <MKBox display="flex" flexDirection="column" alignItems="center" py={1}>
          <MKTypography fontSize="16px" fontWeight="bold" mb={1}>
            {disconnectTarget ? `Disconnect ${disconnectTarget.name}?` : ''}
          </MKTypography>
          <MKTypography fontSize="13px" color="gray" textAlign="center" mb={2}>
            This logs the WhatsApp session out on the feeder. Restoring it later requires scanning a QR code on the phone.
          </MKTypography>
          <MKBox display="flex" gap={1} width="100%">
            <Button fullWidth variant="outlined" onClick={() => setDisconnectTarget(null)}>
              Cancel
            </Button>
            <Button fullWidth variant="gradient" color="error" onClick={onConfirmDisconnect}>
              Disconnect
            </Button>
          </MKBox>
        </MKBox>
      </DialogContent>
    </Dialog>

  </MKBox>;
};

WhatsappSourceAccountListSection.propTypes = {
  title_font_size: PropTypes.string,
  header_color: PropTypes.string,
  text_color: PropTypes.string,
  page_options: PropTypes.string,
  setLoading: PropTypes.func,
  show_header: PropTypes.bool,
  extra_params: PropTypes.object,
  sort_by_options: PropTypes.array,
  currency_options: PropTypes.array,
  source_options: PropTypes.array,
};

WhatsappSourceAccountListSection.defaultProps = {
  title_font_size: '',
  header_color: '#2c589d',
  text_color: '#000',
  page_options: [],
  setLoading: null,
  show_header: false,
  extra_params: {},
  sort_by_options: [],
  currency_options: [],
  source_options: [],
};

export default withLoading(WhatsappSourceAccountListSection);
